import React, { useContext, FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';

// MUI
import Toolbar from '@mui/material/Toolbar';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

// Custom Components
import GridContainer from '@/ui/Grid/GridContainer';
import FormContent from '@/components/FormContent';
import useAuthFormController from '@/components/useAuthFormController';

import DraggableDialog from '@/ui/Dialog/DraggableDialog';
import DialogToolbarHeading from '@/ui/Dialog/DialogToolbarHeading';
import DialogToolbarButtonClose from '@/ui/Dialog/ToolbarButtons/DialogToolbarButtonClose';
import ToolbarFillContent from '@/ui/Toolbar/ToolbarFillContent';
import DialogActionButtonConfirm from '@/ui/Dialog/ActionButtons/DialogActionButtonConfirm';
import DialogActionButtonCancel from '@/ui/Dialog/ActionButtons/DialogActionButtonCancel';
import DialogActionButtonClose from '@/ui/Dialog/ActionButtons/DialogActionButtonClose';

import SnackbarContext from '@/context/SnackbarContext/SnackbarContext';
import DialogContext, { IDialogProps } from '@/context/DialogContext/DialogContext';

// Types
import { FormControllerProps } from '@/@types/components/formController';

const PasswordChangeDialog: FunctionComponent<IDialogProps> = (props) => {
  const { showNotification } = useContext(SnackbarContext);
  const { hideDialog } = useContext(DialogContext);

  const { t } = useTranslation();
  const { form } = props;
  const { record, validation, fields, onFieldChange, doChangePassword } =
    useAuthFormController(props as FormControllerProps);

  const { onClose } = props;

  const handleFieldChange = (value: any, source: string) => {
    if (onFieldChange) {
      onFieldChange(value, source);
    }
  };

  const handleChange = () => {
    if (doChangePassword) {
      doChangePassword().then((result) => {
        if (result.success) {
          close({ success: true });
        } else if (result.error) {
          showNotification(result.error, 'error');
        } else if (result.validationPass === false) {
          console.log(result.validation);
        }
      });
    }
  };

  const handleClose = () => {
    close({ success: false });
  };

  const close = (result: any) => {
    if (onClose) {
      onClose(result);
    }

    hideDialog();
  };

  return (
    <DraggableDialog open maxWidth="xs" onClose={handleClose}>
      <DialogTitle>
        <Toolbar variant="dense" disableGutters>
          <DialogToolbarHeading>
            {t('titles.password_change')}
          </DialogToolbarHeading>
          <ToolbarFillContent />
          <DialogToolbarButtonClose onClick={handleClose} />
        </Toolbar>
      </DialogTitle>
      <DialogContent>
        <GridContainer my={2}>
          <FormContent
            fieldNames={['password', 'password_new', 'password_new_confirm']}
            record={record}
            validation={validation}
            fields={fields}
            onFieldChange={handleFieldChange}
            mode="insert"
            columns={1}
          />
        </GridContainer>
      </DialogContent>
      <DialogActions>
        {form === 'view' ? (
          <DialogActionButtonClose variant="contained" onClick={handleClose} />
        ) : (
          <>
            <DialogActionButtonCancel
              variant="outlined"
              onClick={handleClose}
            />
            <DialogActionButtonConfirm
              variant="contained"
              onClick={handleChange}
            />
          </>
        )}
      </DialogActions>
    </DraggableDialog>
  );
};

export default PasswordChangeDialog;
